"use client";

import { Skeleton } from "@/components/ui/skeleton";

export function DashboardPageSkeleton() {
    return (
        <div className="mx-auto flex min-h-0 w-full max-w-[1400px] min-w-0 flex-col gap-3 md:h-full">
            <section className="grid min-w-0 gap-3 md:grid-cols-3">
                {[0, 1, 2].map((i) => (
                    <article key={i} className="app-panel h-[220px] p-4 2xl:h-[240px]">
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="mt-2 h-3 w-48" />
                        <Skeleton className="mt-4 h-[calc(100%-3rem)] w-full rounded-xl" />
                    </article>
                ))}
            </section>
            <section className="app-panel flex flex-col p-4 md:min-h-0 md:flex-1 md:overflow-hidden">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="mt-4 h-[240px] w-full rounded-xl md:h-auto md:min-h-0 md:flex-1" />
            </section>
        </div>
    );
}

export function AdvancedChartsSkeleton() {
    return (
        <div className="flex flex-col gap-4 md:h-full md:min-h-[360px] md:gap-2 md:p-2">
            {[0, 1, 2, 3].map((i) => (
                <Skeleton
                    key={i}
                    className="h-[168px] w-full shrink-0 rounded-lg md:min-h-0 md:flex-1"
                />
            ))}
        </div>
    );
}

export function AdvancedDashboardSkeleton() {
    return (
        <section className="grid min-w-0 items-start gap-4 md:min-h-0 md:flex-1 md:grid-cols-3 md:items-stretch">
            <article className="app-panel min-w-0 p-4 md:col-span-2 md:min-h-0 md:overflow-hidden">
                <Skeleton className="h-6 w-56" />
                <div className="mt-3 md:min-h-0 md:flex-1">
                    <AdvancedChartsSkeleton />
                </div>
            </article>
            <aside className="flex w-full min-w-0 flex-col gap-4 md:col-span-1 md:h-full md:min-h-0">
                <SolarDiscCardSkeleton />
                <MagnetometerCardSkeleton />
            </aside>
        </section>
    );
}

export function MapPageSkeleton() {
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <Skeleton className="min-h-[60vh] w-full rounded-none md:min-h-0 md:flex-1" />
        </div>
    );
}

export function SunspotMapSkeleton({ className }: { className?: string }) {
    return (
        <Skeleton
            className={[
                "mx-auto aspect-square w-full max-w-[280px] rounded-full",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
        />
    );
}

export function SolarDiscCardSkeleton() {
    return (
        <article className="app-panel min-w-0 p-4">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-28" />
                </div>
                <Skeleton className="h-7 w-20 shrink-0 rounded-md" />
            </div>
            <div className="mt-4">
                <SunspotMapSkeleton />
            </div>
        </article>
    );
}

export function MagnetometerSkeleton() {
    return (
        <div className="flex flex-col gap-3">
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
        </div>
    );
}

export function MagnetometerCardSkeleton() {
    return (
        <article className="app-panel flex min-w-0 flex-col p-4 md:min-h-0 md:flex-1">
            <Skeleton className="h-4 w-44" />
            <Skeleton className="mt-1 h-3 w-36" />
            <Skeleton className="mt-3 h-[220px] w-full shrink-0 rounded-lg md:h-auto md:min-h-0 md:flex-1" />
            <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
                <Skeleton className="h-5 w-full rounded-md" />
                <Skeleton className="h-5 w-full rounded-md" />
            </div>
        </article>
    );
}

export function CmeListSkeleton() {
    return (
        <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
        </div>
    );
}

export function SettingsPageSkeleton() {
    return (
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8">
            <Skeleton className="h-8 w-40" />
            <div className="flex gap-2">
                {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-9 w-24 rounded-md" />
                ))}
            </div>
            <div className="space-y-4">
                <Skeleton className="h-32 w-full rounded-xl" />
                <Skeleton className="h-24 w-full rounded-xl" />
                <Skeleton className="h-40 w-full rounded-xl" />
            </div>
        </div>
    );
}

export function SubmitPageSkeleton() {
    return (
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-8">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-10 w-32 rounded-md" />
        </div>
    );
}

export function AdminPageSkeleton() {
    return (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-8">
            <Skeleton className="h-8 w-36" />
            <div className="grid gap-3 md:grid-cols-2">
                {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-28 w-full rounded-xl" />
                ))}
            </div>
        </div>
    );
}

export function ChartPanelSkeleton({ height = 180 }: { height?: number }) {
    return <Skeleton className="w-full rounded-xl" style={{ height }} />;
}
